import { BasicBarChartConfig } from '@app/data/interface/widget-config';
import {
  BasicBarChartColourPalette,
  BasicBarChartEchartConfigTemplate,
  BasicBarChartYAxisTemplate,
} from '@app/data/widget-config-templates/basic-bar-chart';
import {
  dataViewFormatter,
  formatAxisPointerLabelPercent,
  formatStackedPercentTooltip,
  getTimestampedFileNameForWidget,
  hexToRgba,
} from '@app/widgets/helpers/widgets';
import { EChartsOption, SeriesOption } from 'echarts';
import moment from 'moment';

/**
 * Periphyton Cover Composition Analytes
 */
const periphytonCoverAnalytes = [
  { analyte: 'periphyton-byrophyte', name: 'Byrophyte', color: '#66c39b' },
  { analyte: 'periphyton-long-filaments-brown-red', name: 'Long filaments (brown/red)', color: '#f26b4b' },
  { analyte: 'periphyton-long-filaments-green', name: 'Long filaments (green)', color: '#00916f' },
  { analyte: 'periphyton-medium-mat-black-dark-brown', name: 'Medium mat (black/dark brown)', color: '#26312b' },
  { analyte: 'periphyton-medium-mat-light-brown', name: 'Medium mat (light brown)', color: '#c4906c' },
  { analyte: 'periphyton-thick-mat-black-dark-brown', name: 'Thick mat (black/dark brown)', color: '#000000' },
  { analyte: 'periphyton-film-thin-mat', name: 'Film / thin mat', color: '#a8ceee' },
  { analyte: 'periphyton-medium-mat-green', name: 'Medium mat (green)', color: '#66c39b' },
  { analyte: 'periphyton-thick-mat-green-light-brown', name: 'Thick mat (green / light brown)', color: '#82afdd' },
  { analyte: 'periphyton-short-filaments', name: 'Short filaments', color: '#007093' },
  { analyte: 'periphyton-clean', name: 'Clean', color: '#a8ceee' },
];

/**
 * Periphyton Cover Composition Bar Chart
 *
 * - Stacked bar chart showing the composition of different types of periphyton cover over time.
 * - Each bar represents a sampling event, with segments indicating the percentage cover of each analyte.
 * - The x-axis shows the sampling dates, and the y-axis shows the percentage cover (0-100%).
 * - Tooltips provide detailed information about the composition at each sampling event.
 * - Legend identifies the different types of periphyton cover.
 */
export const buildPeriphytonCoverCompositionConfig = (siteLabel: string, sourceId: string): BasicBarChartConfig => ({
  echartsConfig: {
    ...BasicBarChartEchartConfigTemplate,
    color: periphytonCoverAnalytes.map((entry) => entry.color),
    grid: {
      top: '10%',
      left: '3%',
      right: '7%',
      bottom: '0',
      containLabel: true,
    },
    legend: {
      show: false,
    },
    toolbox: {
      left: 'right',
      top: 0,
      feature: {
        dataView: {
          show: true,
          readOnly: true,
          optionToContent: (option: any): string | HTMLElement => dataViewFormatter(option as EChartsOption),
        },
        saveAsImage: {
          show: true,
          name: getTimestampedFileNameForWidget('periphyton-cover-composition'),
          pixelRatio: 2,
        },
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985',
          formatter: formatAxisPointerLabelPercent,
        },
      },
      formatter: formatStackedPercentTooltip,
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: [],
      axisLine: {
        lineStyle: {
          color: BasicBarChartColourPalette[0],
        },
      },
      axisLabel: {
        rotate: 45,
        color: BasicBarChartColourPalette[0],
        rich: {
          bold: {
            fontWeight: 'bold',
          },
        },
        formatter: (value: any) => {
          const mm = moment(value, 'DD/MM/yyyy', true).isValid() ? moment(value, 'DD/MM/yyyy', true) : moment(value);
          if (!mm.isValid()) return '';
          return mm.format('DD/MM/YYYY');
        },
      },
    },
    yAxis: [
      {
        ...BasicBarChartYAxisTemplate,
        type: 'value',
        name: 'Percent',
        max: 1,
        axisLabel: {
          formatter: (value: any) => {
            if (value === undefined || value === null) return '';
            return Math.round(value * 100) + '%';
          },
          color: BasicBarChartColourPalette[0],
        },
      },
    ],
    series: periphytonCoverAnalytes.map((entry, i) => ({
      name: entry.name,
      type: 'bar',
      stack: 'total',
      barWidth: '60%',
      emphasis: { focus: 'series' },
      itemStyle:
        entry.analyte === 'periphyton-clean'
          ? { color: hexToRgba(entry.color ?? BasicBarChartColourPalette[i % BasicBarChartColourPalette.length], 0.25) }
          : undefined,
      label: {
        show: true,
        formatter: (params: any) => {
          if (params == null || params.value == null) return '';
          return Math.round(params.value * 1000) / 10 + '%';
        },
      },
      encode: { x: 'date_time', y: 'avg' },
      data: [],
    })) as SeriesOption[],
  },
  dataConfig: {
    title: `WAI - Periphyton Cover Composition Over Time - ${siteLabel}`,
    subtitle: '',
    widgetId: 3,
    domain: 'wai',
    groupBy: {
      grouping_type: 'time',
      period: 'quarter',
    },
    showBoundary: true,
    normalize: true,
    hideZeroes: true,
    boundary: 0,
    displayLabelOptions: {
      legendLabelPriority: ['configLabel', 'analyte', 'humanReadableName', 'entity', 'sourceId'],
      includeAnalyte: false,
    },
    preserveConfiguredDataSourceLabels: true,
    dataSources: periphytonCoverAnalytes.map((entry) => ({
      environmentalDataSourceIdentifier: sourceId,
      analyteIdentifier: entry.analyte,
      valueField: 'avg',
      label: entry.name,
    })),
  },
});
